import { Component, OnInit } from '@angular/core';
import { FornecedoresService } from '../fornecedores.service';
import { NotificationService } from '../../../core/notification.service';
import { CategoryService } from '../../categories/category.service';
import { CidadeService } from '../../cidades/cidade.service';
import { CidadeDto } from '../../cidades/cidade.model';
import { getCidadesDisplay, normalizeCidade } from '../../../shared/utils/brasil.utils';

@Component({
  standalone: false,
  selector: 'app-fornecedores-admin-list',
  templateUrl: './fornecedores-admin-list.component.html',
  styleUrls: ['./fornecedores-admin-list.component.css']
})
export class FornecedoresAdminListComponent implements OnInit {
  loading = false;
  page = 1;
  pageSize = 20;
  total = 0;

  // Utility
  Math = Math;

  // Filters (mapped to backend API parameters)
  nome = '';
  categoria = ''; // Maps to categoriaId
  cidade = '';
  /** Filter by city ID (multi-city) — preferred over free-text `cidade` */
  cidadeId = '';
  plano = ''; // Maps to planLevel
  publicado: string = '';
  destaque: string = ''; // "todos", "sim", "nao"
  ativo: string = '';
  claimed: string = ''; // Maps to claimed boolean
  leadsMinimo: string = ''; // Maps to leadsMinimo
  vencendoEmDias: string = ''; // Maps to vencendoEmDias
  orderBy = 'atualizacao'; // Maps to orderBy
  orderDirection = 'desc';

  items: any[] = [];
  categorias: any[] = [];
  cidades: CidadeDto[] = [];

  // Modals
  overrideModalOpen = false;
  overrideFornecedorId = '';
  overridePlanLevel = 'Vitrine';
  overrideExpiryDate = '';
  overrideReason = '';

  constructor(
    private svc: FornecedoresService,
    private notify: NotificationService,
    private catSvc: CategoryService,
    private cidadeSvc: CidadeService
  ) {}

  ngOnInit(): void {
    console.log('[AdminFornecedores] Component initialized');
    this.catSvc.listAll().subscribe((res: any) => {
      this.categorias = (res?.data ?? (Array.isArray(res) ? res : []));
      console.log('[AdminFornecedores] Categories loaded:', this.categorias.length);
    });
    this.cidadeSvc.listAll().subscribe((res: any) => {
      const arr = Array.isArray(res) ? res : (res?.data ?? []);
      this.cidades = arr.map(normalizeCidade);
      console.log('[AdminFornecedores] Cidades loaded:', this.cidades.length);
    }, err => {
      console.warn('[AdminFornecedores] Failed to load cidades (non-critical)', err);
      this.cidades = [];
    });
    this.load();
  }

  load(): void {
    console.log('[AdminFornecedores] Load called with filters:', {
      page: this.page,
      pageSize: this.pageSize,
      nome: this.nome,
      categoriaId: this.categoria,
      cidade: this.cidade,
      cidadeId: this.cidadeId,
      planLevel: this.plano,
      publicado: this.publicado,
      destaque: this.destaque,
      ativo: this.ativo,
      claimed: this.claimed,
      leadsMinimo: this.leadsMinimo,
      vencendoEmDias: this.vencendoEmDias,
      orderBy: this.orderBy,
      orderDirection: this.orderDirection
    });
    this.loading = true;
    this.svc.adminList({
      page: this.page,
      pageSize: this.pageSize,
      nome: this.nome || undefined,
      categoriaId: this.categoria || undefined,
      cidade: this.cidade || undefined,
      cidadeId: this.cidadeId || undefined,
      planLevel: this.plano || undefined,
      publicado: this.publicado ? this.publicado === 'true' : undefined,
      destaque: this.destaque || undefined,
      ativo: this.ativo ? this.ativo === 'true' : undefined,
      claimed: this.claimed ? this.claimed === 'true' : undefined,
      leadsMinimo: this.leadsMinimo ? parseInt(this.leadsMinimo) : undefined,
      vencendoEmDias: this.vencendoEmDias ? parseInt(this.vencendoEmDias) : undefined,
      orderBy: this.orderBy,
      orderDirection: this.orderDirection
    }).subscribe({
      next: (res: any) => {
        console.log('[AdminFornecedores] Response:', res);
        const rawItems = res?.items ?? res?.data ?? [];
        this.items = rawItems.map((item: any) => this.normalizeFornecedor(item));
        this.total = res?.total ?? res?.meta?.total ?? this.items.length;
        console.log('[AdminFornecedores] Items loaded:', this.items.length, 'Total:', this.total);
        this.loading = false;
      },
      error: (err) => {
        console.error('[AdminFornecedores] erro ao listar', err);
        this.notify.showError('Erro ao carregar fornecedores');
        this.loading = false;
      }
    });
  }

  onPageChange(p: number) { this.page = p; this.load(); }

  resetFilters(): void {
    this.nome = '';
    this.categoria = '';
    this.cidade = '';
    this.cidadeId = '';
    this.plano = '';
    this.publicado = '';
    this.destaque = '';
    this.ativo = '';
    this.claimed = '';
    this.leadsMinimo = '';
    this.vencendoEmDias = '';
    this.orderBy = 'atualizacao';
    this.orderDirection = 'desc';
    this.page = 1;
    this.load();
  }

  openOverrideModal(id: string) {
    this.overrideFornecedorId = id;
    this.overridePlanLevel = 'Vitrine';
    this.overrideExpiryDate = '';
    this.overrideReason = '';
    this.overrideModalOpen = true;
  }

  closeOverrideModal() { this.overrideModalOpen = false; }

  applyOverride() {
    if (!this.overrideFornecedorId) return;
    const payload: any = { planLevel: this.overridePlanLevel };
    if (this.overrideExpiryDate) payload.expiryDate = this.overrideExpiryDate;
    if (this.overrideReason) payload.reason = this.overrideReason;
    this.svc.overridePlan(this.overrideFornecedorId, payload).subscribe({
      next: () => {
        this.notify.showSuccess('Plano atualizado com sucesso');
        this.overrideModalOpen = false;
        this.load();
      },
      error: (err: any) => {
        console.error('[AdminFornecedores] erro ao aplicar override', err);
        this.notify.showError('Erro ao aplicar override');
      }
    });
  }

  resetLeads(id: string) {
    if (!confirm('Resetar contador de leads deste fornecedor?')) return;
    this.svc.resetLeads(id).subscribe({
      next: () => {
        this.notify.showSuccess('Contador de leads resetado');
        this.load();
      },
      error: (err) => {
        console.error('[AdminFornecedores] erro ao resetar leads', err);
        this.notify.showError('Erro ao resetar leads');
      }
    });
  }

  toggleDestaque(item: any) {
    const novo = !item.destaque;
    this.svc.setDestaque(item.id, { destaque: novo }).subscribe({
      next: () => {
        item.destaque = novo;
        this.notify.showSuccess('Destaque atualizado');
      },
      error: (err: any) => {
        console.error('[AdminFornecedores] erro ao atualizar destaque', err);
        this.notify.showError('Erro ao atualizar destaque');
      }
    });
  }

  toggleAtivo(item: any) {
    this.svc.toggleActive(item.id).subscribe({
      next: () => {
        item.ativo = !item.ativo;
        item.status = item.ativo ? 'Ativo' : 'Inativo';
        this.notify.showSuccess(item.ativo ? 'Fornecedor ativado' : 'Fornecedor desativado');
      },
      error: (err: any) => {
        console.error('[AdminFornecedores] erro ao alterar ativo', err);
        this.notify.showError('Erro ao alterar status ativo');
      }
    });
  }

  displayEnd(): number { const end = this.page * this.pageSize; return end > this.total ? this.total : end; }

  private normalizeFornecedor(item: any): any {
    const planRaw = String(item?.planLevelText || item?.planLevel || '').toLowerCase();
    const planLevelText = planRaw.includes('vitrine') || planRaw === '1' ? 'Vitrine' : 'Free';

    const isClaimed = item?.isClaimed ?? item?.claimed;
    const status = item?.status || (isClaimed === false ? 'Zumbi' : (item?.ativo ? 'Ativo' : 'Inativo'));

    return {
      ...item,
      planLevelText,
      status,
      isClaimed,
      leadCount: item?.leadCount ?? item?.totalLeads ?? item?.leads ?? 0,
      updatedAt: item?.updatedAt ?? item?.atualizadoEm ?? item?.updatedAtUtc,
      createdAt: item?.createdAt ?? item?.criadoEm,
      vitrineSinceDate: item?.vitrineSinceDate ?? item?.vitrineSince
    };
  }

  countAtivos(): number {
    return this.items.filter(f => f.ativo).length;
  }

  countDestaque(): number {
    return this.items.filter(f => f.destaque).length;
  }

  countZumbi(): number {
    return this.items.filter(f => f.status === 'Zumbi').length;
  }

  /** Returns a display string of cities for a supplier */
  getCidadesDisplay(item: any): string {
    return getCidadesDisplay(item);
  }
}
